/**
 * Helpers for `format: 'ssn'` fields.
 *
 * An SSN is stored and sent as digits only (`111233232`) and always shown grouped (`111-23-3232`),
 * both when an existing number is loaded and as the user types. The field holds the real value, so
 * editing part of it behaves like editing any other text field.
 */

const SSN_DIGIT = /\d/;
const SSN_LENGTH = 9;

export function ssnDigits(value: string | number | null | undefined): string {
  return (value ?? '').toString().replaceAll(/\D/g, '');
}

/**
 * Groups an SSN as `111-23-3232`, dropping anything past 9 digits. Partial input stays partial, so
 * a value halfway through being typed renders as `111-2`.
 */
export function formatSsnForDisplay(value: string | number | null | undefined): string {
  const digits = ssnDigits(value).slice(0, SSN_LENGTH);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** Digit count, used to keep the caret in place across a reformat. */
export function countSsnDigits(value: string): number {
  return ssnDigits(value).length;
}

/** Caret offset in `formatted` that sits just after `count` digits. */
export function ssnCaretPosition(formatted: string, count: number): number {
  if (count <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (SSN_DIGIT.test(formatted[i])) seen++;
    if (seen === count) return i + 1;
  }
  return formatted.length;
}
